import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * VerificationFrame — business verification, inside Verqo.
 *
 * Our bank partner has no API to submit verification, only a hosted form, so
 * it is framed here rather than sending the member off to another site. The
 * dialog polls for the result and closes itself the moment the business is
 * approved.
 */
export default function VerificationFrame({ open, onOpenChange, onApproved }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const poll = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    api.post("/shield/onboarding/link", {})
      .then((r) => { if (!cancelled) setUrl(r.data?.url || null); })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.data?.detail?.code === "business_already_onboarded") {
          toast.success("Your business is already verified.");
          onApproved?.();
          onOpenChange(false);
          return;
        }
        toast.error("Verification is unavailable right now.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      clearInterval(poll.current);
      return undefined;
    }
    poll.current = setInterval(async () => {
      try {
        const r = await api.get("/shield/onboarding/status");
        const s = r.data?.onboarding_status;
        setStatus(s);
        if (s === "approved") {
          clearInterval(poll.current);
          toast.success("Business verified. Your account number is ready.");
          onApproved?.();
          onOpenChange(false);
        } else if (s === "rejected") {
          clearInterval(poll.current);
          toast.error("We could not verify this business. Contact support.");
          onOpenChange(false);
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
    return () => clearInterval(poll.current);
  }, [open, onApproved, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden gap-0"
        data-testid="verification-frame"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Verify your business</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-emerald-700" />
            <div>
              <div className="display text-lg tracking-tight">Verify your business</div>
              <div className="text-xs text-foreground/55">
                Required by our partner bank before your account can hold money.
                {status === "pending" ? " We are reviewing your details." : ""}
              </div>
            </div>
          </div>
        </div>

        {loading || !url ? (
          <div className="flex h-[520px] items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-foreground/40" />
          </div>
        ) : (
          <iframe
            src={url}
            title="Business verification"
            className="h-[520px] w-full border-0"
            data-testid="verification-frame-iframe"
          />
        )}

        <div className="flex items-center justify-between border-t border-border px-6 py-3">
          <span className="text-[11px] text-foreground/45">
            Your details are checked against federal registries. Verqo never sees your ID
            documents.
          </span>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}
                  data-testid="verification-frame-dismiss">
            I'll finish later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
